import { forwardRef, type ReactNode } from 'react';
import {
  formatDate,
  formatTime,
  type TimelineChannel,
  type TimelineKind,
  type TimelineOrigin,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Eyebrow, Skeleton } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import type { TimelineCounts, TimelineFilter, TimelineItem, Tone } from '../types';
import { Badge, OriginChip } from './bits';
import { PortalTimelineEntry } from './PortalTimelineEntry';
import styles from './record.module.css';
import stream from './Timeline.module.css';

export interface TimelineProps {
  /** The record's contact (actions of the portal counterparts). */
  contactId: string;
  counts: TimelineCounts;
  filter: TimelineFilter;
  onFilterChange: (filter: TimelineFilter) => void;
  filterOpen: boolean;
  onToggleFilter: () => void;
  items: TimelineItem[];
  loading: boolean;
  error: unknown;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  /** The block above the stream ("Steht an"). */
  children?: ReactNode | undefined;
}

const CHANNEL_TONE: Record<string, Tone> = {
  call: 'accent',
  email: 'info',
  whatsapp: 'success',
  sms: 'success',
  portal: 'info',
  document: 'warning',
  appointment: 'info',
  meeting: 'info',
  external_appointment: 'info',
  task: 'accent',
  reservation: 'warning',
  self_disclosure: 'info',
  note: 'neutral',
  system: 'neutral',
};

const TYPE_LABEL: Record<string, string> = {
  call: 'Anruf',
  email: 'E-Mail',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  note: 'Notiz',
  appointment: 'Termin',
  meeting: 'Videotermin',
  task: 'Aufgabe',
  document: 'Dokument',
  portal: 'Portal',
  system: 'System',
  self_disclosure: 'Selbstauskunft',
  reservation: 'Reservierung',
  chatbot: 'Chatbot',
  external_appointment: 'Externer Termin',
};

const TONE_VAR: Record<Tone, string> = {
  accent: 'var(--text-accent)',
  info: 'var(--info-fg)',
  success: 'var(--success-fg)',
  warning: 'var(--warning-fg)',
  danger: 'var(--danger-fg)',
  neutral: 'var(--sand-400)',
};

function channelDot(key: string): string {
  if (key === 'all') return 'var(--sand-50)';
  return TONE_VAR[CHANNEL_TONE[key] ?? 'neutral'];
}

/**
 * "Anruf — Nicht erreicht · Versuch 2" → "Nicht erreicht · Versuch 2" when the kind chip already says "Anruf";
 * likewise "Externer Termin · Kundentermin · …" → "Kundentermin · …".
 */
function stripKind(title: string, kindLabel: string): string {
  for (const prefix of [`${kindLabel} — `, `${kindLabel} · `]) {
    if (title.startsWith(prefix)) return title.slice(prefix.length);
  }
  return title;
}

function dateTime(iso: string): string {
  return `${formatDate(iso)}, ${formatTime(iso)}`;
}

function Ticks({ item }: { item: TimelineItem }) {
  if (item.direction !== 'outbound') return null;
  const read = item.status === 'read';
  const double = read || item.status === 'delivered';
  return (
    <span className={clsx(stream.tick, read && stream.tickRead)} aria-label={item.statusLabel ?? undefined}>
      {double ? '✓✓' : '✓'}
    </span>
  );
}

function Bubble({ item }: { item: TimelineItem }) {
  const out = item.direction === 'outbound';
  return (
    <div className={clsx(stream.row, out && stream.rowEnd)}>
      <div className={stream.bubbleWrap}>
        <div className={clsx(stream.bubble, out ? stream.bubbleOut : stream.bubbleIn)}>
          <div className={stream.bubbleText}>{item.text ?? item.title}</div>
          <div className={stream.bubbleFoot}>
            <span className={clsx(stream.bubbleOrigin, item.originLabel !== 'Kontakt' && stream.accent)}>
              {item.originLabel}
            </span>
            <span className={stream.bubbleTime}>{formatTime(item.occurredAt)}</span>
            <Ticks item={item} />
          </div>
        </div>
        <div className={clsx(stream.bubbleBy, out && stream.rowEnd)}>
          <span>{item.actor.name}</span>
          <span>·</span>
          <span>{formatDate(item.occurredAt)}</span>
        </div>
      </div>
    </div>
  );
}

function MailCard({ item }: { item: TimelineItem }) {
  const out = item.direction === 'outbound';
  return (
    <div className={clsx(stream.row, out && stream.rowEnd)}>
      <div className={stream.mail}>
        <div className={stream.mailHead}>
          <span className={stream.mailEyebrow}>E-Mail</span>
          <span className={stream.mailRoute}>
            {out ? `von ${item.actor.name} · an den Kontakt` : `von ${item.actor.name} · an uns`}
          </span>
          <span className={stream.mailDate}>{dateTime(item.occurredAt)}</span>
        </div>
        <div className={stream.mailBody}>
          {item.subject ? <div className={stream.mailSubject}>{item.subject}</div> : null}
          {item.text ? <div className={stream.mailText}>{item.text}</div> : null}
        </div>
        <div className={stream.mailFoot}>
          <OriginChip label={item.originLabel} />
          {item.meta ? <span className={stream.small}>{item.meta}</span> : null}
        </div>
      </div>
    </div>
  );
}

function PortalCard({ item }: { item: TimelineItem }) {
  const out = item.direction === 'outbound';
  return (
    <div className={clsx(stream.row, out && stream.rowEnd)}>
      <div className={stream.portal}>
        <span className={stream.portalRail} />
        <div className={stream.portalBody}>
          <div className={stream.portalEyebrow}>Kundenportal</div>
          <div className={stream.portalText}>{item.text ?? item.title}</div>
          <div className={stream.chipRow}>
            <OriginChip label={item.originLabel} sunken />
            <span className={stream.small}>{item.meta ?? dateTime(item.occurredAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecordRow({ item }: { item: TimelineItem }) {
  const kindLabel = TYPE_LABEL[item.type] ?? item.type;
  const tone = CHANNEL_TONE[item.type] ?? 'neutral';
  const done = item.statusLabel === 'erledigt';
  return (
    <div className={stream.record} style={{ borderLeftColor: TONE_VAR[tone] }}>
      <span className={clsx(styles.kindChip, kindClass(tone))}>{kindLabel}</span>
      <div className={stream.recordText}>
        <div className={clsx(stream.recordTitle, done && stream.strike)}>
          {stripKind(item.title, kindLabel)}
        </div>
        <div className={stream.chipRow}>
          <span className={stream.small}>{dateTime(item.occurredAt)}</span>
          <OriginChip label={item.originLabel} />
          {item.text ? <span className={stream.small}>{item.text}</span> : null}
        </div>
      </div>
      {item.statusLabel ? <Badge tone={item.tone ?? 'neutral'}>{item.statusLabel}</Badge> : null}
    </div>
  );
}

function CallCard({ item }: { item: TimelineItem }) {
  const rail = TONE_VAR[item.tone ?? 'accent'];
  return (
    <div className={stream.call}>
      <span className={stream.callRail} style={{ background: rail }} />
      <div className={stream.callBody}>
        <div className={stream.callHead}>
          <span className={stream.callEyebrow} style={{ color: rail }}>
            {TYPE_LABEL[item.type] ?? 'Termin'}
          </span>
          {item.statusLabel ? <Badge tone={item.tone ?? 'neutral'}>{item.statusLabel}</Badge> : null}
          <span className={stream.callDate}>{dateTime(item.occurredAt)}</span>
        </div>
        <div className={stream.callTitle}>{item.title}</div>
        <div className={stream.chipRow}>
          <OriginChip label={item.originLabel} sunken />
          {item.text ? <span className={stream.small}>{item.text}</span> : null}
        </div>
      </div>
    </div>
  );
}

function SystemRow({ item }: { item: TimelineItem }) {
  return (
    <div className={stream.system}>
      <span className={stream.systemDot} />
      <span className={stream.systemText}>{item.title}</span>
      <OriginChip label={item.originLabel} sunken />
      <span className={stream.small}>
        {[item.text, dateTime(item.occurredAt)].filter(Boolean).join(' · ')}
      </span>
    </div>
  );
}

function kindClass(tone: Tone): string | undefined {
  return {
    accent: styles.kindAccent,
    info: styles.kindInfo,
    warning: styles.kindWarning,
    danger: styles.kindDanger,
    success: styles.kindSuccess,
    neutral: styles.kindNeutral,
  }[tone];
}

export function TimelineEntry({ item }: { item: TimelineItem }) {
  switch (item.kind) {
    case 'bubble':
      return <Bubble item={item} />;
    case 'mail':
      return <MailCard item={item} />;
    case 'portal':
      return <PortalCard item={item} />;
    case 'call':
      return <CallCard item={item} />;
    case 'system':
      return <SystemRow item={item} />;
    case 'record':
      return <RecordRow item={item} />;
  }
}

function FilterChipRow<T extends string>({
  title,
  entries,
  value,
  onChange,
  dots,
}: {
  title: string;
  entries: TimelineCounts['channel'];
  value: T | null;
  onChange: (value: T | null) => void;
  dots: (key: string) => string;
}) {
  return (
    <div className={styles.filterGroup}>
      <div className={styles.eyebrowSmall}>{title}</div>
      <div className={styles.filterChips} role="group" aria-label={title}>
        {entries.map((entry) => {
          const active = entry.key === 'all' ? value === null : value === entry.key;
          return (
            <button
              key={entry.key}
              type="button"
              aria-pressed={active}
              className={clsx(styles.filterChip, active && styles.filterChipActive)}
              onClick={() => onChange(entry.key === 'all' ? null : (entry.key as T))}
            >
              <span className={styles.filterDot} style={{ background: dots(entry.key) }} />
              {entry.label}
              <span className={styles.filterCount}>{entry.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function labelOf(entries: TimelineCounts['channel'], key: string | null): string | null {
  return key === null ? null : (entries.find((entry) => entry.key === key)?.label ?? key);
}

function summary(counts: TimelineCounts, filter: TimelineFilter, total: number): string {
  const channel = labelOf(counts.channel, filter.channel);
  const kind = labelOf(counts.kind, filter.kind);
  const origin = labelOf(counts.origin, filter.origin);
  const parts = [
    channel ? `Kanal ${channel}` : 'Alle Kanäle',
    kind ? `Art ${kind}` : 'alle Arten',
    origin ? `Herkunft ${origin}` : 'alle Herkünfte',
  ];
  const text = parts.join(', ');
  return `${text.charAt(0).toUpperCase()}${text.slice(1)} — ${total} ${total === 1 ? 'Ereignis' : 'Ereignisse'}`;
}

/** The timeline card of the record: filter panel (I.05), „Steht an", the stream in its component kinds. */
export const Timeline = forwardRef<HTMLDivElement, TimelineProps>(function Timeline(
  {
    contactId,
    counts,
    filter,
    onFilterChange,
    filterOpen,
    onToggleFilter,
    items,
    loading,
    error,
    hasMore,
    loadingMore,
    onLoadMore,
    children,
  },
  ref,
) {
  const total = counts.channel.find((entry) => entry.key === 'all')?.count ?? 0;
  const filtered = filter.channel !== null || filter.kind !== null || filter.origin !== null;
  return (
    <section ref={ref} className={styles.card} aria-label="Timeline">
      <div className={styles.timelineHead}>
        <Eyebrow size={11}>Timeline · Kontakt und alle zugeordneten Objekte</Eyebrow>
        <span className={styles.timelineCount}>
          {total} {total === 1 ? 'Ereignis' : 'Ereignisse'}
        </span>
        <Button
          variant={filterOpen ? 'secondary' : 'ghost'}
          size="small"
          onClick={onToggleFilter}
          aria-expanded={filterOpen}
        >
          {filterOpen ? 'Filter schließen' : 'Filter'}
        </Button>
      </div>
      {filterOpen ? (
        <div className={styles.filterPanel}>
          <FilterChipRow<TimelineChannel>
            title="Kanal"
            entries={counts.channel}
            value={filter.channel}
            onChange={(channel) => onFilterChange({ ...filter, channel })}
            dots={channelDot}
          />
          <FilterChipRow<TimelineKind>
            title="Art"
            entries={counts.kind}
            value={filter.kind}
            onChange={(kind) => onFilterChange({ ...filter, kind })}
            dots={(key) => (key === 'all' ? 'var(--sand-50)' : 'var(--sand-400)')}
          />
          <FilterChipRow<TimelineOrigin>
            title="Herkunft"
            entries={counts.origin}
            value={filter.origin}
            onChange={(origin) => onFilterChange({ ...filter, origin })}
            dots={(key) => (key === 'all' ? 'var(--sand-50)' : 'var(--sand-400)')}
          />
          <div className={styles.filterFoot}>
            <span className={styles.filterSummary}>
              {summary(counts, filter, filtered ? items.length : total)}
            </span>
            <span className={styles.filterActions}>
              <Button
                variant="ghost"
                size="small"
                disabled={!filtered}
                onClick={() => onFilterChange({ channel: null, kind: null, origin: null })}
              >
                Zurücksetzen
              </Button>
            </span>
          </div>
        </div>
      ) : null}
      {children}
      <div className={styles.divider}>
        <span className={styles.dividerLine} />
        <span className={styles.eyebrowSmall}>Verlauf</span>
        <span className={styles.dividerLine} />
      </div>
      {error ? <Banner tone="danger">{errorMessage(error)}</Banner> : null}
      {loading ? (
        <div className={styles.stream} aria-busy="true">
          <Skeleton variant="block" height={64} />
          <Skeleton variant="block" height={48} />
          <Skeleton width="60%" />
        </div>
      ) : (
        <div className={styles.stream}>
          {items.length === 0 && !error ? (
            <div className={styles.streamEmpty}>
              {filtered ? 'Keine Ereignisse für diesen Filter.' : 'Noch keine Ereignisse.'}
            </div>
          ) : null}
          {items.map((item, index) =>
            item.portal ? (
              <PortalTimelineEntry
                key={item.id}
                item={{ ...item, portal: item.portal }}
                contactId={contactId}
                connect={Boolean(items[index + 1]?.portal)}
              />
            ) : (
              <TimelineEntry key={item.id} item={item} />
            ),
          )}
        </div>
      )}
      {hasMore ? (
        <div className={styles.moreRow}>
          <Button variant="secondary" size="small" loading={loadingMore} onClick={onLoadMore}>
            Weitere laden
          </Button>
        </div>
      ) : null}
    </section>
  );
});
